import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DepartmentInterface } from "../../../models/TicketModule/Department";
import departmentService from "../../../services/ticketService/departmentService";
import { CACHE_KEY_DEPARTMENTS } from "../../../utils/constants";

interface DeleteDepartmentContext {
  previousDepartments: DepartmentInterface[];
  deletedDepartment: DepartmentInterface | undefined;
}

const useDeleteDepartment = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    void, // No return data after deletion
    Error,
    string, // ID of the department to delete
    DeleteDepartmentContext
  >({
    mutationFn: departmentService.Remove, // Remove should take a string ID

    onMutate: (departmentId: string) => {
      const previousDepartments =
        queryClient.getQueryData<DepartmentInterface[]>([
          CACHE_KEY_DEPARTMENTS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      // Optimistically update the cache
      queryClient.setQueryData<DepartmentInterface[]>(
        [
          CACHE_KEY_DEPARTMENTS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        previousDepartments.filter(
          (department) => department.id !== departmentId
        ) // Use departmentId to filter
      );

      return {
        previousDepartments,
        deletedDepartment: previousDepartments.find(
          (department) => department.id === departmentId
        ),
      };
    },

    onError: (error, departmentId, context) => {
      if (!context) return;

      // Rollback to the previous state in case of error
      queryClient.setQueryData<DepartmentInterface[]>(
        [
          CACHE_KEY_DEPARTMENTS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousDepartments
      );
    },

    onSuccess: () => {
      // Optionally, you could perform additional actions on success
      // For example, invalidating queries or updating other parts of the state
    },
  });
};

export default useDeleteDepartment;
